import { inject } from "vue"
import {
  createRouter,
  createWebHistory,
  type NavigationGuard,
  type RouteLocationGeneric,
  type RouteRecordRaw
} from "vue-router"

import { Role } from "@/utils/UserRoles"
import EconOverview from "@/views/EconOverview.vue"
import EconFundamentals from "@/views/EconFundamentals.vue"
import EconValuationTool from "@/views/EconValuationTool.vue"
import EconTechnicals from "@/views/EconTechnicals.vue"
import EconESG from "@/views/EconESGNew.vue"
import CorpValuation from "@/views/CorpValuation.vue"
import CorpOverview from "@/views/CorpOverview.vue"
import CorpModelView from "@/views/CorpModelView.vue"
import Home from "@/views/Home.vue"

const checkPermission: NavigationGuard = (_to, _from, next) => {
  const roles = inject<Role[]>("roles") ?? []
  if (roles.includes(Role.Admin)) {
    next()
  } else {
    next("/not-authorized")
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    component: Home,
    meta: { transitionName: "slide", menuOrder: 0 }
  },
  {
    path: "/sovereign",
    name: "sovereign-home",
    component: () => import("../views/SovereignHome.vue")
  },
  {
    path: "/corp",
    name: "corp-home",
    component: () => import("../views/CorpHome.vue")
  },
  {
    path: "/taa-scorecard",
    name: "top-down-taa-scorecard",
    component: () => import("../views/taa/Taa.vue")
  },
  {
    path: "/econ-summary",
    name: "top-down-econ-summary",
    component: () => import("../views/topDown/EconSummary.vue")
  },
  {
    path: "/attribution",
    name: "attribution",
    beforeEnter(_to, _from, next) {
      if (window.appConfig.VITE_Show_Poc_Features === "true") {
        next()
      } else {
        next("/not-authorized")
      }
    },
    component: () => import("../views/attribution/Attribution.vue")
  },
  {
    path: "/portfolio-management",
    name: "portfolio-management",
    redirect: "",
    beforeEnter: () => {
      const url = "http://emdrebaltool.blairnet.net/"
      //since it is a intermediate page for redireaction,
      //use location.replace here to avoid http://host/portfolio-management to be saved in the history
      //which will cause the back button to go back to the intermediate page
      location.replace(url)
    }
  },
  {
    path: "/investable-universe",
    name: "investable-universe",
    component: () => import("../views/InvestableUniverse.vue")
  },
  {
    path: "/countries/:region/econ",
    alias: "/countries/:region/sovereign",
    component: () => import("../views/Sovereign.vue"),
    props: true,
    children: [
      { name: "econ", path: "", component: EconOverview, props: true },
      { name: "econ-overview", path: "overview", component: EconOverview, props: true },
      { name: "econ-fundamentals", path: "fundamentals", component: EconFundamentals, props: true },
      { name: "econ-valuations", path: "valuations", component: EconValuationTool, props: true },
      { name: "econ-technicals", path: "technicals", component: EconTechnicals, props: true },
      {
        name: "econ-sustainability",
        path: "sustainability",
        component: EconESG,
        props: true
      }
    ].map((d, i) => ({
      ...d,
      meta: {
        //transitionName: "slide",
        menuOrder: 20 + i,
        keyGen($route: RouteLocationGeneric) {
          // remove the last segment for nested route
          // note fullPath is start with /, so need slice 4
          const parentKey = $route.fullPath.split("/").slice(0, 4).join("/")
          return parentKey
        }
      }
    }))
  },
  {
    path: "/econ-export",
    name: "export",
    component: () => import("../views/EconDataExport.vue"),
    props: true
  },
  {
    path: "/corps/:issuer?",
    name: "corp-detail",
    component: () => import("../views/CorpDetail.vue"),
    props: true,
    meta: { transitionName: "slide", menuOrder: 40 },
    children: [
      { name: "corp-overview", path: "overview", component: CorpOverview, props: true },
      { name: "corp-risk-model", path: "risk-model", component: CorpModelView, props: true },
      {
        path: "esg",
        component: () => import("../views/corpEsg/Home.vue"),
        props: true,
        children: <RouteRecordRaw[]>[
          {
            name: "corp-esg",
            path: "",
            props: true,
            redirect: {
              name: "corp-esg-overview"
            }
          },
          {
            path: "overview",
            name: "corp-esg-overview",
            component: () => import("../views/corpEsg/Overview.vue"),
            props: true
          },
          {
            path: "pbi-dashboard",
            name: "corp-pbi-esg-dashboard",
            component: () => import("../views/corpEsg/PbiDashboard.vue"),
            props: true
          },
          {
            path: "scores",
            name: "corp-esg-issuer-scores",
            component: () => import("../views/corpEsg/Scores.vue"),
            props: true
          },
          {
            path: "factor-materialities/:sectorGroup",
            name: "issuer-corp-esg-factor-materialities",
            component: () => import("../views/corpEsg/FactorMaterialities.vue"),
            props(route: RouteLocationGeneric) {
              return {
                sectorGroup: route.params.sectorGroup
              }
            }
          }
        ]
      },
      {
        name: "corp-valuation",
        path: "valuation",
        component: CorpValuation,
        props: true
      }
    ].map((d, i) => ({
      ...d,
      meta: {
        transitionName: "slide",
        menuOrder: 40 + i,
        keyGen($route: RouteLocationGeneric) {
          const parentKey = $route.fullPath.split("/").slice(0, 3).join("/")
          return parentKey
        }
      }
    }))
  },
  {
    path: "/corp-sectors",
    name: "corp-sectors",
    component: () => import("../views/CorpHome.vue"),
    props: true,
    meta: { transitionName: "slide", menuOrder: 30 }
  },
  {
    path: "/corp-sectors/:sector",
    name: "corp-sector",
    redirect(to) {
      return {
        name: "corp-sector-issuers",
        params: { sector: to.params.sector }
      }
    }
  },
  {
    path: "/corp-sectors/:sector/issuers",
    name: "corp-sector-issuers",
    component: () => import("../views/CorpSectorIssuers.vue"),
    props: true,
    meta: { transitionName: "slide", menuOrder: 32 }
  },
  {
    path: "/corp-sectors/:sector/bonds",
    name: "corp-sector-bonds",
    component: () => import("../views/CorpSectorBonds.vue"),
    props: true,
    meta: { transitionName: "slide", menuOrder: 32 }
  },
  {
    path: "/factor-materialities/:sectorGroup",
    name: "corp-esg-factor-materialities",
    meta: { transitionName: "slide", menuOrder: 34 },
    component: () => import("../views/corpEsg/FactorMaterialities.vue"),
    props: true
  },
  {
    path: "/weekly-scores/hc",
    name: "weekly-scores-hc",
    component: () => import("../views/weeklyScores/Hc.vue"),
    beforeEnter: checkPermission
  },
  {
    path: "/weekly-scores/lc",
    name: "weekly-scores-lc",
    component: () => import("../views/weeklyScores/Lc.vue"),
    beforeEnter: checkPermission
  },
  {
    path: "/not-authorized",
    component: () => import("@/views/NotAuthorized.vue")
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { behavior: "smooth", left: 0, top: 0 }
    }
  },
  routes
})

export default router
